import React, { createContext, useContext, useEffect, useState, useMemo } from "react";
import { useAuth } from "@/context/AuthContext";

const ProjectFilterContext = createContext(null);
const KEY_BASE = "sumadhura.projectSelection.v1";

/**
 * Global project multi-select + period granularity shared across Dashboard,
 * Analytics & Budget Plan. Selection persists to localStorage (per user).
 * `selected = []` means "all projects".
 */
export function ProjectFilterProvider({ children }) {
  const { user } = useAuth();
  const uid = user?.id || "anon";
  const storeKey = `${KEY_BASE}.${uid}`;

  const [selected, setSelected] = useState([]); // [] => all
  const [gran, setGran] = useState("Yearly"); // Yearly | Half-yearly | Quarterly | Monthly
  const [quarter, setQuarter] = useState("Q1");
  const [half, setHalf] = useState("H1"); // H1 = Q1+Q2 (Apr-Sep), H2 = Q3+Q4 (Oct-Mar)
  const [month, setMonth] = useState("Apr-26");
  const [months, setMonths] = useState([]); // multi-month selection (Monthly mode); [] => use single `month`
  const [remember, setRemember] = useState(true);
  const [hydrated, setHydrated] = useState(false);

  // hydrate on user change
  useEffect(() => {
    setHydrated(false);
    try {
      const raw = localStorage.getItem(storeKey);
      if (raw) {
        const s = JSON.parse(raw);
        setSelected(Array.isArray(s.selected) ? s.selected : []);
        setGran(s.gran || "Yearly");
        setQuarter(s.quarter || "Q1");
        setHalf(s.half || "H1");
        setMonth(s.month || "Apr-26");
        setMonths(Array.isArray(s.months) ? s.months : []);
        setRemember(s.remember !== false);
      } else {
        setSelected([]); setGran("Yearly"); setQuarter("Q1"); setHalf("H1"); setMonth("Apr-26"); setMonths([]); setRemember(true);
      }
    } catch (e) { /* noop */ }
    setHydrated(true);
  }, [storeKey]);

  // persist
  useEffect(() => {
    if (!hydrated) return;
    if (!remember) { try { localStorage.removeItem(storeKey); } catch (e) {} return; }
    try {
      localStorage.setItem(storeKey, JSON.stringify({ selected, gran, quarter, half, month, months, remember }));
    } catch (e) { /* noop */ }
  }, [selected, gran, quarter, half, month, months, remember, hydrated, storeKey]);

  const period = useMemo(
    () => (gran === "Yearly" ? "FY"
      : gran === "Half-yearly" ? half
        : gran === "Quarterly" ? quarter
          : (months && months.length ? months.join(",") : month)),
    [gran, quarter, half, month, months]
  );

  const value = {
    selected, setSelected,
    gran, setGran, quarter, setQuarter, half, setHalf, month, setMonth, months, setMonths,
    remember, setRemember,
    period, hydrated,
  };
  return <ProjectFilterContext.Provider value={value}>{children}</ProjectFilterContext.Provider>;
}

export function useProjectFilter() {
  const ctx = useContext(ProjectFilterContext);
  if (!ctx) throw new Error("useProjectFilter must be used within ProjectFilterProvider");
  return ctx;
}
